import { getConversationHistoryApi, getConversationListApi } from '@/apis/conversation'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import useThemeStore from './themeStore'

const useChatStore = defineStore('newans-chat', () => {
  const themeStore = useThemeStore()

  /**
   * 对话列表
   */
  const conversationList = ref<IConversation[]>([])

  /**
   * 对话列表分页信息
   */
  const conversationPage = ref(1)
  const conversationHasMore = ref(true)
  const conversationLoading = ref(false)

  /**
   * 获取对话列表
   */
  const getConversationList = async (page = 1) => {
    if (conversationLoading.value) return

    if (page === 1) {
      conversationPage.value = 1
      conversationHasMore.value = true
    }

    if (!conversationHasMore.value) return

    try {
      conversationLoading.value = true
      const res = await getConversationListApi({ page, limit: 30 })
      const records = res.data?.records || []

      if (page === 1) {
        conversationList.value = records
      } else {
        conversationList.value.push(...records)
      }

      conversationPage.value = page
      conversationHasMore.value = records.length >= 30

      if (currentConversationId.value === 0 && conversationList.value.length > 0) {
        setCurrentConversationId(conversationList.value[0]?.id || 0)
      }
    } finally {
      conversationLoading.value = false
    }
  }

  /**
   * 加载更多对话列表
   */
  const loadMoreConversations = async () => {
    if (!conversationHasMore.value) return
    await getConversationList(conversationPage.value + 1)
  }

  /**
   * 当前对话ID
   */
  const currentConversationId = ref<number>(0)

  /**
   * 当前对话的消息历史
   */
  const conversationHistory = ref<IChatMessage[]>([])

  /**
   * 面板消息索引
   */
  const panelMessageIndex = ref<number>(0)

  /**
   * 是否显示更多面板
   */
  const showMorePanel = ref<boolean>(false)

  /**
   * 制作视频并循环播放时：隐藏左侧对话区、地图面板拉满；地图组件内用 fixed 定位铺满视口（非 Fullscreen API）
   */
  const mapPanelVideoImmersiveMode = ref(false)

  const setMapPanelVideoImmersive = (v: boolean) => {
    mapPanelVideoImmersiveMode.value = v
  }

  /**
   * 更多面板标题
   */
  const morePanelTitle = ref<string>('')

  /**
   * 更多面板类型
   */
  const morePanelType = ref<string>('')

  /**
   * 当前聚焦事件ID 从事件聚焦工具调用开始
   */
  const currentFocusEventId = ref<string>('')

  /**
   * 是否正在遍历聚焦事件
   */
  const isFocusEventIterating = ref<boolean>(false)

  let focusEventIterationTimers: ReturnType<typeof setTimeout>[] = []

  const stopFocusEventIteration = () => {
    focusEventIterationTimers.forEach((timer) => clearTimeout(timer))
    focusEventIterationTimers = []
    isFocusEventIterating.value = false
  }

  /** 由地图面板「循环播放」等逻辑设置，用于禁用图中点击与列表点击 */
  const setFocusEventIterating = (v: boolean) => {
    isFocusEventIterating.value = v
  }

  /**
   * 面板图表选项数据
   */
  const panelChartOptionData = computed(() => {
    console.log(morePanelType.value, panelMessageIndex.value)
    // if (morePanelType.value !== 'echarts_option_json') return null

    if (!conversationHistory.value[panelMessageIndex.value]) return null

    const part = conversationHistory.value[panelMessageIndex.value]!.parts.find(
      (part) => part.fastGptModuleName === 'echarts_option_json',
    )
    if (!part) return null
    try {
      console.log(JSON.parse(part.ansText))
      return JSON.parse(part.ansText)
    } catch (error) {
      console.error(error)
      return null
    }
  })

  /**
   * 面板地图选项数据
   */
  const panelEventJsonData = computed(() => {
    console.log('panelEventJsonData', morePanelType.value, panelMessageIndex.value)
    if (!['event_json', 'graph_chart_json'].includes(morePanelType.value)) return null
    if (!conversationHistory.value[panelMessageIndex.value]) return null
    const part = conversationHistory.value[panelMessageIndex.value]!.parts.find(
      (part) => part.fastGptModuleName === 'event_json',
    )
    if (!part) return null
    try {
      console.log(JSON.parse(part.ansText))
      return JSON.parse(part.ansText)
    } catch (error) {
      console.error(error)
      return null
    }
  })

  /**
   * 面板图表选项数据
   */
  const panelGraphChartJsonData = computed(() => {
    console.log('panelEventJsonData', morePanelType.value, panelMessageIndex.value)

    if (!['event_json', 'graph_chart_json'].includes(morePanelType.value)) return null

    if (!conversationHistory.value[panelMessageIndex.value]) return null

    const part = conversationHistory.value[panelMessageIndex.value]!.parts.find(
      (part) => part.fastGptModuleName === 'graph_chart_json',
    )
    console.log('part', part)
    if (!part) return null
    try {
      console.log(JSON.parse(part.ansText))
      return JSON.parse(part.ansText)
    } catch (error) {
      console.error(error)
      return null
    }
  })

  /**
   * 当前对话的消息历史
   * @param history 消息历史
   */
  const loadConversationHistory = () => {
    if (!currentConversationId.value) return
    getConversationHistoryApi(currentConversationId.value).then((res) => {
      conversationHistory.value = res.data || []
      if (res.data && res.data.length > 0) {
        const lastMessage = res.data[res.data.length - 1]
        if (
          lastMessage &&
          lastMessage.parts &&
          lastMessage.parts[lastMessage.parts.length - 1]?.fastGptModuleName === 'focus_event'
        ) {
          openMapPanelWithFocusEvent(lastMessage.parts[lastMessage.parts.length - 1]?.ansText || '')
        }
      }
    })
  }

  /**
   * 最后一条消息
   */
  const lastHistoryMessage = computed(() => {
    return conversationHistory.value[conversationHistory.value.length - 1] || null
  })

  /**
   * 最后一条消息的最后一个部分
   */
  const lastHistoryMessageLastPart = computed(() => {
    if (!lastHistoryMessage.value) return null
    return lastHistoryMessage.value.parts[lastHistoryMessage.value.parts.length - 1] || null
  })

  /**
   * 设置当前对话ID
   * @param id 对话ID
   */
  const setCurrentConversationId = (id: number) => {
    const isDifferent = currentConversationId.value !== id
    currentConversationId.value = id
    if (isDifferent) {
      conversationHistory.value = []
      loadConversationHistory()
    }
  }

  /**
   * 清空对话
   */
  const clearConversations = () => {
    conversationList.value = []
    currentConversationId.value = 0
    conversationHistory.value = []
  }

  /**
   * 添加消息
   * @param message 消息
   */
  const addMessage = (message: IChatMessage) => {
    conversationHistory.value.push(message)
  }

  /**
   * 打开更多面板
   * @param type 类型
   * @param historyMessageIndex 消息索引
   */
  const openMorePanel = (type: string, historyMessageIndex: number) => {
    showMorePanel.value = true
    themeStore.setSideCollapsed(true)
    morePanelType.value = type
    panelMessageIndex.value = historyMessageIndex
    switch (type) {
      case 'event_json':
      case 'graph_chart_json':
        morePanelTitle.value = '事件详情'
        break
      case 'echarts_option_json':
        morePanelTitle.value = '统计图表'
        break
    }
  }

  /**
   * 在消息历史中查找包含指定 eventId 的消息索引
   */
  const findMessageIndexByEventId = (eventId: string): number => {
    for (let i = 0; i < conversationHistory.value.length; i++) {
      const message = conversationHistory.value[i]
      if (message && message.parts.some((part) => part.fastGptModuleName === 'event_json')) {
        if (JSON.stringify(message.parts).includes(eventId)) {
          return i
        }
      }
    }
    return -1
  }

  /**
   * 打开地图面板并遍历聚焦事件
   * @param eventJson 事件数据，格式为 [{"eventId": "xxx"}, ...] 的 JSON 字符串
   */
  const openMapPanelWithFocusEvent = (eventJson: string) => {
    stopFocusEventIteration()

    const eventArray: { eventId: string }[] = JSON.parse(eventJson)

    if (!Array.isArray(eventArray) || eventArray.length === 0) return

    const firstMessageIndex = findMessageIndexByEventId(eventArray[0]!.eventId)
    if (firstMessageIndex === -1) return

    showMorePanel.value = true
    themeStore.setSideCollapsed(true)
    morePanelType.value = 'event_json'
    morePanelTitle.value = '事件详情'
    panelMessageIndex.value = firstMessageIndex

    isFocusEventIterating.value = true

    eventArray.forEach((eventObj, index) => {
      const delay = 500 + index * 5000
      const timer = setTimeout(() => {
        const msgIndex = findMessageIndexByEventId(eventObj.eventId)
        if (msgIndex !== -1 && panelMessageIndex.value !== msgIndex) {
          panelMessageIndex.value = msgIndex
        }

        currentFocusEventId.value = eventObj.eventId

        if (index === eventArray.length - 1) {
          isFocusEventIterating.value = false
        }
      }, delay)

      focusEventIterationTimers.push(timer)
    })
  }

  /**
   * 关闭更多面板
   */
  const closeMorePanel = () => {
    stopFocusEventIteration()
    mapPanelVideoImmersiveMode.value = false
    showMorePanel.value = false
    themeStore.setSideCollapsed(themeStore.currentWindowWidth < 1200)
  }

  watch(
    () => currentConversationId.value,
    (newVal) => {
      if (themeStore.currentWindowWidth < 1200 || showMorePanel.value) {
        themeStore.setSideCollapsed(true)
      }
      if (!newVal) {
        return
      }
      loadConversationHistory()
    },
    { immediate: true },
  )

  getConversationList()

  return {
    loadConversationHistory,
    lastHistoryMessage,
    lastHistoryMessageLastPart,
    currentConversationId,
    setCurrentConversationId,
    conversationHistory,
    conversationList,
    getConversationList,
    conversationPage,
    conversationHasMore,
    loadMoreConversations,
    addMessage,
    clearConversations,
    openMorePanel,
    closeMorePanel,
    showMorePanel,
    mapPanelVideoImmersiveMode,
    setMapPanelVideoImmersive,
    morePanelTitle,
    morePanelType,
    panelChartOptionData,
    panelMessageIndex,
    panelEventJsonData,
    panelGraphChartJsonData,
    openMapPanelWithFocusEvent,
    currentFocusEventId,
    isFocusEventIterating,
    stopFocusEventIteration,
    setFocusEventIterating,
  }
})

export default useChatStore
